/**
 * auth 域云函数 Web 端封装（add-auth-login）。
 */
import type {
  ChangePasswordInput,
  ChangePasswordOutput,
  LoginOutput,
  WxWebLoginInput,
  LogoutOutput,
  MeOutput,
  WebPasswordLoginInput,
  WebPasswordLoginOutput,
  WebPasswordLoginSuccess,
  WebPasswordSelectInput,
} from '@cms/shared'
import { callFunction, drainEmployeeRequestsForLogout } from '../utils/tcb'
import { getSessionToken } from '../utils/auth-session'

const pendingLogouts = new Map<string | null, Promise<LogoutOutput>>()

/** 开始 Web 密码认证；可能直接登录，也可能只返回本次已验证的企业候选。 */
export async function loginWithPassword(input: WebPasswordLoginInput): Promise<WebPasswordLoginOutput> {
  return callFunction<WebPasswordLoginOutput>('auth-web-password-login', {
    data: input as unknown as Record<string, unknown>,
  })
}

/** 使用内存中的一次性 selectionToken 确认服务端候选，不接受 tenantId。 */
export async function selectPasswordTenant(input: WebPasswordSelectInput): Promise<WebPasswordLoginSuccess> {
  return callFunction<WebPasswordLoginSuccess>('auth-web-password-select', {
    data: input as unknown as Record<string, unknown>,
  })
}

/** 使用微信开放平台网页登录 code 登录已绑定的员工账号。 */
export async function loginWithWebWechat(input: WxWebLoginInput): Promise<LoginOutput> {
  return callFunction<LoginOutput>('wx-web-login', {
    data: input as unknown as Record<string, unknown>,
  })
}

export function logout(): Promise<LogoutOutput> {
  const token = getSessionToken()
  const pending = pendingLogouts.get(token)
  if (pending) return pending
  // 快速重复点击共享同一次退出，不能在业务请求收束或服务端撤销等待期间再次撤销旧 token。
  const request = (async (): Promise<LogoutOutput> => {
    await drainEmployeeRequestsForLogout(token)
    return callFunction<LogoutOutput>('auth-logout', {
      data: {},
    }, { token, redirectOnAuthError: false })
  })().finally(() => {
    if (pendingLogouts.get(token) === request) pendingLogouts.delete(token)
  })
  pendingLogouts.set(token, request)
  return request
}

export async function me(): Promise<MeOutput> {
  return callFunction<MeOutput>('auth-me', {
    data: {},
  })
}

export async function changePassword(input: ChangePasswordInput): Promise<ChangePasswordOutput> {
  return callFunction<ChangePasswordOutput>('auth-change-password', {
    data: input as unknown as Record<string, unknown>,
  })
}
